import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetIntakeOrgServicesQuery, useSetIntakeOrgServicesMutation } from '../store/api';

import {
  type IntakeServiceTier,
  type IntakeSetupService,
  IntakeSetupServicesPage,
} from './four-stage/IntakeSetupServicesPage';
import {
  INTAKE_SERVICE_OPTIONS,
  IntakeServiceSelectionCard,
  type SelectableIntakeService,
} from './IntakeServiceSelectionCard';

const selectableServices = new Set<string>(INTAKE_SERVICE_OPTIONS.map(option => option.id));
/** Answers recorded on the declaration rather than enrolled as services (GDN-3741). */
const ADDON_KEYS = ['jotassure'] as const;
type IntakeAddon = (typeof ADDON_KEYS)[number];
const isAddon = (id: string): id is IntakeAddon => (ADDON_KEYS as readonly string[]).includes(id);

export function ProviderIntakeServices({
  onSaved,
  standalone = false,
  layout = 'card',
  onTierChange,
  onContinued,
  failureCount,
  onFailureCountChange,
}: {
  onSaved?: () => Promise<unknown>;
  standalone?: boolean;
  /** `setup` renders the Agreements & setup services page and saves on Continue. */
  layout?: 'card' | 'setup';
  /** Fires on every tier pick; the saved tier itself comes back from the org-services read. */
  onTierChange?: (tier: IntakeServiceTier) => void;
  /** Called after a successful save from the setup page's Continue. */
  onContinued?: () => void;
  failureCount?: number;
  onFailureCountChange?: (count: number) => void;
}) {
  const {
    currentData: data,
    error: loadError,
    isFetching,
    refetch,
  } = useGetIntakeOrgServicesQuery();
  const [setServices] = useSetIntakeOrgServicesMutation();
  const { intakeHub } = useEventTracking();
  const saving = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  // On the enrollment gate a checkbox change only edits this draft until
  // Continue; the embedded card saves on change like the rest of the intake.
  const [draft, setDraft] = useState<SelectableIntakeService[] | null>(null);
  // Tier and add-ons are answers on the declaration; the draft holds them
  // until Continue, then the read returns what was stored.
  const savedTier: IntakeServiceTier = data?.billing_tier ?? 'managed';
  const [tierDraft, setTierDraft] = useState<IntakeServiceTier | null>(null);
  const tier = tierDraft ?? savedTier;
  const [addonsDraft, setAddonsDraft] = useState<IntakeAddon[] | null>(null);
  const savedAddons = (data?.addons ?? []).filter(isAddon);
  const addons = addonsDraft ?? savedAddons;
  const draftMode = standalone || layout === 'setup';
  const [pendingServices, setPendingServices] = useState<SelectableIntakeService[] | null>(null);
  const [error, setError] = useState<string>();
  const [localFailedCount, setFailedCount] = useState(0);
  const failedCount = failureCount ?? localFailedCount;
  const [confirmation, setConfirmation] = useState<SelectableIntakeService[] | null>(null);
  const services = INTAKE_SERVICE_OPTIONS.filter(option =>
    data?.service_keys?.includes(option.id),
  ).map(option => option.id);
  const canManage = Boolean(data?.can_manage_services) && !loadError;
  const selection = pendingServices ?? draft ?? services;
  // The gate's PUT also creates the caller's enrollment anchor, so an
  // organization that already chose services must still be able to continue.
  const canContinue = selection.length > 0;

  const save = async (
    next: SelectableIntakeService[],
    { confirmRemove = false, keepDraft = false } = {},
  ) => {
    if (!canManage || saving.current) return;
    saving.current = true;
    setIsSaving(true);
    setPendingServices(next);
    setError(undefined);
    intakeHub.serviceSelectionChanged({
      service_keys: next.join(','),
      selected_count: next.length,
    });
    let persisted = false;
    try {
      const result = await setServices({
        organizationEnrollServicesRequest: {
          service_keys: next,
          confirm_remove: confirmRemove,
          // Only the services page states the tier; the card leaves it alone.
          ...(layout === 'setup'
            ? { billing_tier: tier, addons: tier === 'managed' ? addons : [] }
            : {}),
        },
      }).unwrap();
      persisted = true;
      setConfirmation(null);
      if (!keepDraft) {
        setDraft(null);
        setTierDraft(null);
        setAddonsDraft(null);
      }
      const failures = result.failed_user_ids?.length ?? 0;
      setFailedCount(failures);
      onFailureCountChange?.(failures);
      await refetch().unwrap();
      await onSaved?.();
    } catch (cause) {
      const response =
        cause && typeof cause === 'object' && 'data' in cause ? cause.data : undefined;
      const code =
        response && typeof response === 'object' && 'error_code' in response
          ? response.error_code
          : undefined;
      if (code === 'intake_service_removal_unconfirmed') {
        setConfirmation(next);
      } else {
        setConfirmation(null);
        const conflict =
          cause && typeof cause === 'object' && 'status' in cause && cause.status === 409;
        setError(
          persisted
            ? 'Your services were saved, but we could not refresh this view. Reload to see your selection.'
            : conflict
              ? 'Services changed while you were saving. Review the current selection and try again.'
              : code === 'intake_service_selection_empty'
                ? 'Choose at least one service to start intake for your practice.'
                : 'We could not save your services. Reload the current selection and try again.',
        );
        if ((persisted || conflict) && !keepDraft) setDraft(null);
        await refetch();
      }
    } finally {
      saving.current = false;
      setIsSaving(false);
      setPendingServices(null);
    }
    return persisted;
  };
  // The setup page advances only after a real save, including one that first
  // needed the removal confirmation. `onContinued` is unset for the card layout.
  const saveAndContinue = async (
    next: SelectableIntakeService[],
    options: { confirmRemove?: boolean } = {},
  ) => {
    if (await save(next, options)) onContinued?.();
  };
  return (
    <Box sx={standalone ? { maxWidth: 600, mx: 'auto', px: 3, py: 6 } : undefined}>
      <Stack spacing={2}>
        {loadError ? (
          <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
            We could not load your organization&apos;s services. Changes are disabled until they
            load.
          </Alert>
        ) : !data ? (
          <LinearProgress aria-label="Loading organization services" />
        ) : null}
        {data && layout === 'setup' && (
          <IntakeSetupServicesPage
            tier={tier}
            onTierChange={next => {
              setTierDraft(next);
              onTierChange?.(next);
              intakeHub.serviceTierSelected({ tier: next });
              // Self-Pay is JotBill itself; JotCred is its only add-on.
              if (next === 'self_pay') {
                setDraft(selection.includes('jotcred') ? ['jotbill', 'jotcred'] : ['jotbill']);
                setAddonsDraft([]);
              }
            }}
            services={[...selection, ...(tier === 'managed' ? addons : [])]}
            onServicesChange={(next: IntakeSetupService[]) => {
              setDraft(
                next.filter((id): id is SelectableIntakeService => selectableServices.has(id)),
              );
              setAddonsDraft(next.filter(isAddon));
            }}
            canManage={canManage}
            isSaving={isSaving || isFetching}
            continueDisabled={!canContinue}
            onContinue={() => void saveAndContinue(draft ?? services)}
          />
        )}
        {data && layout === 'card' && (
          <>
            <IntakeServiceSelectionCard
              services={selection}
              canManage={canManage}
              isSaving={isSaving || isFetching}
              onChange={next => (draftMode ? setDraft(next) : void save(next))}
            />
            {standalone && canManage && (
              <Box>
                <Button
                  variant="contained"
                  disabled={isSaving || isFetching || !canContinue}
                  onClick={() => void save(draft ?? services)}
                >
                  Continue
                </Button>
              </Box>
            )}
          </>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {failedCount > 0 && (
          <Alert severity="warning">
            Services were saved, but {failedCount} provider journey{failedCount === 1 ? '' : 's'}{' '}
            could not be updated.
            <Button disabled={isSaving} onClick={() => void save(services, { keepDraft: true })}>
              Retry provider setup
            </Button>
          </Alert>
        )}
      </Stack>
      <Dialog open={confirmation !== null} onClose={() => !isSaving && setConfirmation(null)}>
        <DialogTitle>Remove services already used by your practice?</DialogTitle>
        <DialogContent>
          This selection removes a service your providers already use. Its questions will be hidden
          and existing answers kept.
        </DialogContent>
        <DialogActions>
          <Button disabled={isSaving} onClick={() => setConfirmation(null)}>
            Cancel
          </Button>
          <Button
            disabled={isSaving}
            onClick={() =>
              confirmation && void saveAndContinue(confirmation, { confirmRemove: true })
            }
          >
            Confirm removal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
